// ABOUTME: React Context for managing the SignalR connection to the family hub.
// ABOUTME: Provides family-scoped and child-scoped real-time event subscription via the useSignalR hook.

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { getCurrentAccessToken } from '../services/api';

// Matches /family/:familyId/:slug/child/:childName/:token
//      and /ohana/:familyId/:slug/child/:childName/:token
const CHILD_ROUTE_PATTERN = /^\/(family|ohana)\/[^/]+\/([^/]+)\/child\/[^/]+\/([^/]+)/;

function parseChildRoute(pathname: string): { slug: string; token: string } | null {
  const match = CHILD_ROUTE_PATTERN.exec(pathname);
  if (!match) return null;
  return { slug: match[2], token: match[3] };
}

export interface SignalRContextValue {
  /**
   * Subscribe to a SignalR event. Returns an unsubscribe function.
   * Safe to call before the connection is established — handlers are registered
   * on the connection object and will fire once the connection starts.
   */
  on: (eventName: string, handler: (...args: unknown[]) => void) => () => void;
  /** Current connection state, or 'Disconnected' if not yet started */
  connectionState: signalR.HubConnectionState;
}

const SignalRContext = createContext<SignalRContextValue | null>(null);

export interface SignalRProviderProps {
  /** Whether the user is authenticated. Connection is only established when true. */
  isAuthenticated: boolean;
  children: ReactNode;
}

export const SignalRProvider: React.FC<SignalRProviderProps> = ({
  isAuthenticated,
  children,
}) => {
  const location = useLocation();
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  // Pending handlers registered before the connection is established.
  // Map of eventName → Set of handlers.
  const pendingHandlersRef = useRef<Map<string, Set<(...args: unknown[]) => void>>>(new Map());
  const [connectionState, setConnectionState] =
    React.useState<signalR.HubConnectionState>(
      signalR.HubConnectionState.Disconnected
    );

  const updateState = useCallback((state: signalR.HubConnectionState) => {
    setConnectionState(state);
  }, []);

  useEffect(() => {
    const childRoute = parseChildRoute(location.pathname);
    const shouldConnect = isAuthenticated || !!childRoute;

    if (!shouldConnect) {
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        updateState(signalR.HubConnectionState.Disconnected);
      }
      return;
    }

    const hubUrl = childRoute
      ? `/hubs/family?slug=${encodeURIComponent(childRoute.slug)}&token=${encodeURIComponent(childRoute.token)}`
      : '/hubs/family';

    const connectionOptions = childRoute
      ? {}
      : { accessTokenFactory: () => getCurrentAccessToken() ?? '' };

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, connectionOptions)
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    // Register any handlers that were queued before the connection was created.
    // Child component useEffects run before parent useEffects on mount, so handlers
    // may be registered via on() before the connection object exists.
    pendingHandlersRef.current.forEach((handlers, eventName) => {
      handlers.forEach(handler => connection.on(eventName, handler));
    });

    connectionRef.current = connection;

    connection.onreconnecting(() =>
      updateState(signalR.HubConnectionState.Reconnecting)
    );
    connection.onreconnected(() =>
      updateState(signalR.HubConnectionState.Connected)
    );
    connection.onclose(() =>
      updateState(signalR.HubConnectionState.Disconnected)
    );

    // Handle background disconnect and foreground re-connection via Page Visibility API.
    // A 60-second debounce prevents unnecessary disconnects from quick tab switches.
    let backgroundDisconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (backgroundDisconnectTimer !== null) {
          clearTimeout(backgroundDisconnectTimer);
        }
        backgroundDisconnectTimer = setTimeout(() => {
          connectionRef.current?.stop();
        }, 60_000);
      } else {
        // Tab became visible — cancel any pending background disconnect
        if (backgroundDisconnectTimer !== null) {
          clearTimeout(backgroundDisconnectTimer);
          backgroundDisconnectTimer = null;
        }
        // Reconnect if the connection was stopped while in the background
        if (
          connectionRef.current?.state ===
          signalR.HubConnectionState.Disconnected
        ) {
          connectionRef.current.start().catch(() => {
            // Silently ignore — automatic reconnect will retry
          });
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    updateState(signalR.HubConnectionState.Connecting);
    connection.start()
      .then(() => updateState(signalR.HubConnectionState.Connected))
      .catch(() => updateState(signalR.HubConnectionState.Disconnected));

    return () => {
      if (backgroundDisconnectTimer !== null) {
        clearTimeout(backgroundDisconnectTimer);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      connection.stop();
      connectionRef.current = null;
      updateState(signalR.HubConnectionState.Disconnected);
    };
  }, [isAuthenticated, location.pathname, updateState]);

  const on = useCallback(
    (eventName: string, handler: (...args: unknown[]) => void): (() => void) => {
      // Always add to the pending map so the handler is registered when the
      // connection is (re-)created, even if on() is called before it exists.
      if (!pendingHandlersRef.current.has(eventName)) {
        pendingHandlersRef.current.set(eventName, new Set());
      }
      pendingHandlersRef.current.get(eventName)!.add(handler);

      // If the connection already exists, register immediately too.
      connectionRef.current?.on(eventName, handler);

      return () => {
        pendingHandlersRef.current.get(eventName)?.delete(handler);
        connectionRef.current?.off(eventName, handler);
      };
    },
    []
  );

  const value: SignalRContextValue = { on, connectionState };

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};

export const useSignalR = (): SignalRContextValue => {
  const context = useContext(SignalRContext);
  if (context === null) {
    throw new Error('useSignalR must be used within a SignalRProvider');
  }
  return context;
};

export default SignalRContext;
